import { asc, eq, inArray } from "drizzle-orm";
import {
  block,
  type Block,
  page,
  type PageVersion,
  type PowerSyncDrizzleDB,
} from "@folio/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { isSidecarBlockType } from "../documents/kinds";
import {
  patchRowsVerified,
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
  waitForRowsPresent,
} from "../powersync/verified-write";
import { sortKeyBetween } from "./sort-key";
import { collectDescendantPageIds, isPageInSubtree } from "./tree";

/**
 * Every `UPDATE … WHERE id = ?` on `page`/`block` in this file goes through
 * `patchRowVerified`/`patchRowsVerified`/`waitForRowPresent(s)`: a raw UPDATE
 * against the PowerSync view can match ZERO rows and return "success" while
 * the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). Mutations **can throw**: the caller
 * must report it, never swallow it.
 */

/**
 * Rows the editor-content diff may see. SIDECAR rows (comment threads, …)
 * are NOT editor content: the autosave/restore diff must neither delete nor
 * reorder them (they have their own mutations in `comments/mutations.ts`).
 */
export function contentBlockRows<T extends { type: string | null }>(rows: T[]): T[] {
  return rows.filter((row) => !isSidecarBlockType(row.type));
}

/**
 * Creates a new page. Returns its id.
 */
export async function createPageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  opts?: {
    title?: string;
    icon?: string;
    parentPageId?: string;
    /** Page key after which to insert (null = at the end). */
    afterSortKey?: string | null;
    beforeSortKey?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(page).values({
    id,
    user_id: userId,
    title: opts?.title ?? "",
    icon: opts?.icon ?? null,
    parent_page_id: opts?.parentPageId ?? null,
    sort_key: sortKeyBetween(opts?.afterSortKey ?? null, opts?.beforeSortKey ?? null),
    is_shared: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Creates a local-first office DOCUMENT (spreadsheet or presentation): a
 * `page` (reusing title/trash/history/sharing) + ONE serialized content
 * block (blob) whose `id === page.id` and whose `type` is the document
 * `kind` (see `documents/kinds.ts`). Returns the page id.
 *
 * The block is born in the SAME transaction as the page, so the server treats
 * it as a "page created in this batch" and does not archive the noisy "empty"
 * version (see `db/sync.rs`). Later autosave = upsert of the SAME block (stable
 * id).
 */
export async function createDocumentPageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  kind: string,
  opts: { title?: string; content: string; text?: string },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.transaction(async (tx) => {
    await tx.insert(page).values({
      id,
      user_id: userId,
      title: opts.title ?? "",
      icon: null,
      parent_page_id: null,
      sort_key: sortKeyBetween(null, null),
      is_shared: 0,
      created_at: now,
      updated_at: now,
    });
    await tx.insert(block).values({
      // The content block id IS the page id: stable blob identity (one
      // document = exactly one block), diff-by-id without churn.
      id,
      page_id: id,
      user_id: userId,
      type: kind,
      content: opts.content,
      text: opts.text ?? "",
      sort_key: "a0",
      is_shared: 0,
      created_at: now,
      updated_at: now,
    });
  });
  return id;
}

/**
 * Updates an existing page.
 */
export async function updatePageMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    title: string | null;
    icon: string | null;
    parent_page_id: string | null;
    sort_key: string;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, page, page.id, id, updates, options);
}

/**
 * PHYSICALLY deletes a page, its blocks, and all its subpages (recursively).
 * This is the trash's "delete permanently": it is irreversible and also
 * cascades the version history (FK in Postgres).
 * In Postgres the server performs the cascade; in local SQLite we do it
 * explicitly so the UI reflects the deletion immediately.
 */
export async function deletePageMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const allPages = await db.select({ id: page.id, parent_page_id: page.parent_page_id }).from(page);
  const toDelete = collectDescendantPageIds(allPages, id);

  await db.transaction(async (tx) => {
    await tx.delete(block).where(inArray(block.page_id, toDelete));
    await tx.delete(page).where(inArray(page.id, toDelete));
  });
}

/**
 * Trash (L2): soft-delete the page and its ENTIRE still-live subtree with
 * the SAME timestamp (cohort). Restoring the parent restores exactly its
 * cohort; a subpage sent to trash EARLIER keeps its own timestamp and is not
 * restored by the sweep.
 */
export async function movePageToTrashMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const allPages = await db
    .select({ id: page.id, parent_page_id: page.parent_page_id, deleted_at: page.deleted_at })
    .from(page);
  const subtree = new Set(collectDescendantPageIds(allPages, id));
  const alive = allPages
    .filter((row) => subtree.has(row.id) && row.deleted_at === null)
    .map((row) => row.id);
  if (alive.length === 0) {
    return;
  }
  const now = nowISO();
  await patchRowsVerified(db, page, page.id, alive, { deleted_at: now }, options);
}

/**
 * Restores a page from the trash along with the cohort of subpages that went
 * with it (same deleted_at). If its parent no longer exists or is still in
 * the trash, the restored page moves to the root (it does not remain an
 * invisible orphan).
 */
export async function restorePageFromTrashMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const allPages = await db
    .select({ id: page.id, parent_page_id: page.parent_page_id, deleted_at: page.deleted_at })
    .from(page);
  const target = allPages.find((row) => row.id === id);
  if (!target || target.deleted_at === null) {
    return;
  }
  const cohortTs = target.deleted_at;
  const subtree = new Set(collectDescendantPageIds(allPages, id));
  const cohort = allPages
    .filter((row) => subtree.has(row.id) && row.deleted_at === cohortTs)
    .map((row) => row.id);

  const parent = allPages.find((row) => row.id === target.parent_page_id);
  const orphaned = target.parent_page_id !== null && (!parent || parent.deleted_at !== null);

  // The transaction cannot retry (it would sleep with the write lock held), so
  // the wait is OUTSIDE: first confirm that the ENTIRE cohort is in the view,
  // then open the transaction; inside it writes without checking again (see
  // `../powersync/verified-write.ts`).
  await waitForRowsPresent(db, page, page.id, cohort, options);

  const now = nowISO();
  await db.transaction(async (tx) => {
    await tx
      .update(page)
      .set({ deleted_at: null, updated_at: now })
      .where(inArray(page.id, cohort));
    if (orphaned) {
      await tx.update(page).set({ parent_page_id: null, updated_at: now }).where(eq(page.id, id));
    }
  });
}

/**
 * Moves a page to another parent and/or position. Ignores the operation if
 * it would create a cycle (dropping a page inside its own subtree).
 */
export async function movePageMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  target: {
    parentPageId: string | null;
    /** Key of the previous sibling at the destination (null = first). */
    afterSortKey?: string | null;
    /** Key of the next sibling (null = last). */
    beforeSortKey?: string | null;
  },
  options?: VerifiedWriteOptions,
): Promise<void> {
  if (target.parentPageId) {
    const allPages = await db
      .select({ id: page.id, parent_page_id: page.parent_page_id })
      .from(page);
    if (isPageInSubtree(allPages, pageId, target.parentPageId)) {
      return;
    }
  }

  await patchRowVerified(
    db,
    page,
    page.id,
    pageId,
    {
      parent_page_id: target.parentPageId,
      sort_key: sortKeyBetween(target.afterSortKey ?? null, target.beforeSortKey ?? null),
    },
    options,
  );
}

/**
 * Top-level editor node, ready to persist as a block.
 */
export interface EditorBlockNode {
  /** Stable block UUID (attrs.blockId of the TipTap node). */
  id: string;
  /** TipTap node type (paragraph, heading, bulletList, …). */
  type: string;
  /** Serialized JSON of the complete node. */
  content: string;
  /** Plain text extracted from the node (LIKE search without JSON noise). */
  text: string;
}

/**
 * Synchronizes a page's blocks with the editor's current state.
 *
 * "One block = one row" strategy: diff by id between the top-level nodes of
 * the TipTap document and the local rows. Only changed blocks are written
 * (insert/update/delete), so editing a paragraph generates ONE sync operation,
 * not a document rewrite.
 */
export async function syncPageBlocksMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  userId: string,
  nodes: EditorBlockNode[],
  options?: VerifiedWriteOptions,
): Promise<void> {
  // Sidecar rows (comment threads) are invisible to the content diff: they
  // must survive every autosave untouched.
  const existing: Block[] = contentBlockRows(
    await db
      .select()
      .from(block)
      .where(eq(block.page_id, pageId))
      .orderBy(asc(block.sort_key), asc(block.created_at)),
  );

  const existingById = new Map(existing.map((row) => [row.id, row]));
  const keptIds = new Set(nodes.map((node) => node.id));

  const removedIds = existing.filter((row) => !keptIds.has(row.id)).map((row) => row.id);

  const now = nowISO();

  // The transaction below cannot retry (it would sleep with the write lock
  // held): re-confirm the presence of the blocks this save will treat as
  // UPDATEs (those found by the SELECT above) immediately before opening the
  // transaction — closing the gap between that read and the write if the window
  // opened right in the middle (see `../powersync/verified-write.ts`). Truly
  // new ids (absent from `existingById`) are not waited for: waiting for a
  // block that never existed would hang autosave until the backoff expires.
  await waitForRowsPresent(db, block, block.id, [...existingById.keys()], options);

  // One local transaction ⇒ ONE PowerSync transactionId ⇒ the server
  // processes the entire save as one group (and version history archives ONE
  // version per save, not one per touched block).
  await db.transaction(async (tx) => {
    if (removedIds.length > 0) {
      await tx.delete(block).where(inArray(block.id, removedIds));
    }

    let prevKey: string | null = null;

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (!node) {
        continue;
      }
      const current = existingById.get(node.id);
      let key = current?.sort_key ?? null;

      // The existing key is useful only if it respects document order
      const keyIsValid = key !== null && (prevKey === null || key > prevKey);
      if (!keyIsValid) {
        // Upper bound: the key of the next surviving block that still
        // orders after prevKey (if there is none, insert "at the end").
        let upper: string | null = null;
        for (let j = i + 1; j < nodes.length; j++) {
          const nextNode = nodes[j];
          const candidate = nextNode ? (existingById.get(nextNode.id)?.sort_key ?? null) : null;
          if (candidate !== null && (prevKey === null || candidate > prevKey)) {
            upper = candidate;
            break;
          }
        }
        key = sortKeyBetween(prevKey, upper);
      }

      if (!current) {
        await tx.insert(block).values({
          id: node.id,
          page_id: pageId,
          user_id: userId,
          type: node.type,
          content: node.content,
          text: node.text,
          sort_key: key,
          is_shared: 0,
          created_at: now,
          updated_at: now,
        });
      } else if (
        current.content !== node.content ||
        current.type !== node.type ||
        current.sort_key !== key
      ) {
        await tx
          .update(block)
          .set({
            type: node.type,
            content: node.content,
            text: node.text,
            sort_key: key,
            updated_at: now,
          })
          .where(eq(block.id, node.id));
      }

      prevKey = key;
    }
  });
}

/** Block as it travels inside a version's `blocks` snapshot. */
interface VersionBlockNode {
  id: string;
  type: string;
  content: string | null;
  text: string | null;
  sort_key: string;
}

function isNullOrString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isVersionBlockNode(value: unknown): value is VersionBlockNode {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const node: Partial<Record<keyof VersionBlockNode, unknown>> = value;
  return (
    typeof node.id === "string" &&
    typeof node.type === "string" &&
    typeof node.sort_key === "string" &&
    isNullOrString(node.content) &&
    isNullOrString(node.text)
  );
}

/** Parses the `blocks` snapshot (JSON from the local bucket or the API). */
export function parseVersionBlocks(blocks: string | null): VersionBlockNode[] {
  if (!blocks) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(blocks);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((node) => isVersionBlockNode(node));
  } catch {
    return [];
  }
}

/**
 * Restores a history version (L4): a NORMAL forward write.
 *
 * History is never rewritten: the server write path archives the current
 * state as a new version BEFORE applying this (forced by the
 * `restored_from_version` marker, which skips autosave's temporal collapse).
 *
 * The `page` UPDATE comes FIRST in the transaction: its op reaches the server
 * before the block changes, so the pre-restore snapshot captures the blocks
 * while they are still intact.
 */
export async function restorePageVersionMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  version: Pick<PageVersion, "id" | "title" | "icon" | "blocks">,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const [pageRow] = await db
    .select({ user_id: page.user_id })
    .from(page)
    .where(eq(page.id, pageId))
    .limit(1);
  if (!pageRow) {
    return;
  }
  // Restore must neither wipe live comment threads nor resurrect the ones
  // captured in the snapshot: sidecar rows are excluded on BOTH sides.
  const snapshot = parseVersionBlocks(version.blocks).filter(
    (node) => !isSidecarBlockType(node.type),
  );
  const existing: Block[] = contentBlockRows(
    await db.select().from(block).where(eq(block.page_id, pageId)),
  );
  const existingById = new Map(existing.map((row) => [row.id, row]));
  const keptIds = new Set(snapshot.map((node) => node.id));
  const removedIds = existing.filter((row) => !keptIds.has(row.id)).map((row) => row.id);

  // The transaction below cannot retry. Re-confirm the presence of the page
  // (already located above) and the blocks that will be treated as UPDATEs
  // immediately before opening the transaction (see
  // `../powersync/verified-write.ts`).
  await waitForRowPresent(db, page, page.id, pageId, options);
  await waitForRowsPresent(db, block, block.id, [...existingById.keys()], options);

  const now = nowISO();
  await db.transaction(async (tx) => {
    await tx
      .update(page)
      .set({
        title: version.title ?? "",
        icon: version.icon,
        restored_from_version: version.id,
        updated_at: now,
      })
      .where(eq(page.id, pageId));

    if (removedIds.length > 0) {
      await tx.delete(block).where(inArray(block.id, removedIds));
    }
    for (const node of snapshot) {
      const current = existingById.get(node.id);
      if (!current) {
        await tx.insert(block).values({
          id: node.id,
          page_id: pageId,
          user_id: pageRow.user_id,
          type: node.type,
          content: node.content,
          text: node.text,
          sort_key: node.sort_key,
          is_shared: 0,
          created_at: now,
          updated_at: now,
        });
      } else if (
        current.content !== node.content ||
        current.type !== node.type ||
        current.text !== node.text ||
        current.sort_key !== node.sort_key
      ) {
        await tx
          .update(block)
          .set({
            type: node.type,
            content: node.content,
            text: node.text,
            sort_key: node.sort_key,
            updated_at: now,
          })
          .where(eq(block.id, node.id));
      }
    }
  });
}
